import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from 'hooks/sympl-query';
import { GET_VACANCY_VIDEO_LOCKS } from 'graphql/vacancies/queries';
import {
  applyVideoLockMessage,
  isVideoLockMessage,
  NO_VIDEO_LOCKS,
  openVideoLockChannel,
  readVideoLocks,
  VideoGenerationLocks,
  VideoLockProvider,
} from 'utils/videoGenerationLocks';

interface VacancyVideoLocksResponse {
  vacancyVideoLocks: { video_generation_locks?: unknown } | null;
}

/**
 * SYM-5391. The lock state of the vacancy in the editor, kept true three ways:
 *
 * 1. read from the server when the editor loads (and on every `refetch`, which the Bring to
 *    Life entry calls before opening, so a stale tab cannot start a second generation);
 * 2. set locally the moment THIS tab starts or finishes a generation, so the button reacts
 *    without waiting for the server;
 * 3. told by OTHER tabs over a BroadcastChannel, so two tabs on one vacancy agree within the
 *    time it takes the message to arrive.
 */
export const useVideoGenerationLocks = (
  vacancyId: number | null | undefined
) => {
  const [locks, setLocks] = useState<VideoGenerationLocks>(NO_VIDEO_LOCKS);
  const channelRef = useRef<BroadcastChannel | null>(null);

  const { data, refetch: refetchQuery } = useQuery<
    VacancyVideoLocksResponse,
    { vacancyId: number }
  >(GET_VACANCY_VIDEO_LOCKS, {
    skip: !vacancyId,
    variables: { vacancyId: vacancyId as number },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (data?.vacancyVideoLocks) {
      setLocks(readVideoLocks(data.vacancyVideoLocks.video_generation_locks));
    }
  }, [data]);

  // A new vacancy starts from "unlocked" until the server answers for it.
  useEffect(() => {
    setLocks(NO_VIDEO_LOCKS);
  }, [vacancyId]);

  useEffect(() => {
    const channel = openVideoLockChannel();
    channelRef.current = channel;
    if (!channel) return undefined;
    const onMessage = (event: MessageEvent) => {
      if (!isVideoLockMessage(event.data)) return;
      setLocks((current) =>
        applyVideoLockMessage(current, event.data, vacancyId)
      );
    };
    channel.addEventListener('message', onMessage);
    return () => {
      channel.removeEventListener('message', onMessage);
      channel.close();
      channelRef.current = null;
    };
  }, [vacancyId]);

  /** Asks the server again and returns the fresh answer; the caller decides what to do with it. */
  const refetch = useCallback(async (): Promise<VideoGenerationLocks> => {
    if (!vacancyId) return NO_VIDEO_LOCKS;
    try {
      const result = await refetchQuery({ vacancyId });
      const fresh = readVideoLocks(
        result.data?.vacancyVideoLocks?.video_generation_locks
      );
      setLocks(fresh);
      return fresh;
    } catch {
      return locks; // a failed refetch keeps what we had; the server's 409 remains the fail-safe
    }
  }, [vacancyId, refetchQuery, locks]);

  /** This tab started or finished a generation: apply here and tell the other tabs. */
  const setLocked = useCallback(
    (provider: VideoLockProvider, locked: boolean) => {
      if (!vacancyId) return;
      setLocks((current) =>
        current[provider] === locked
          ? current
          : { ...current, [provider]: locked }
      );
      try {
        channelRef.current?.postMessage({ vacancyId, provider, locked });
      } catch {
        // a closed channel is not worth an error; the refetch on open covers it
      }
    },
    [vacancyId]
  );

  return { locks, refetch, setLocked };
};
